/**
 * Single source of truth for accepted file types in the classification upload feature.
 *
 * Document types = final files sent to the classifier (PDF, images).
 * Container types = archives extracted client-side before upload (ZIP).
 */

// ── Document types (accepted by classifier) ────────────────────────

export const DOCUMENT_EXTENSIONS = new Set(["pdf", "png", "jpg", "jpeg"]);

export const DOCUMENT_MIME_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
] as const;

export const EXTENSION_TO_MIME: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
};

// ── Container types (extracted client-side, not uploaded directly) ──

export const ZIP_MIME_TYPES = new Set([
  "application/zip",
  "application/x-zip-compressed",
  "application/x-zip",
  "application/octet-stream",
]);

// ── Size limits ────────────────────────────────────────────────────

export const MAX_ZIP_SIZE = 500 * 1024 * 1024; // 500 MB per ZIP archive

// ── Upload batching (mirrors services/api/app/schemas/classifications.py) ──

export const UPLOAD_CONCURRENCY = 5; // Max parallel GCS uploads to avoid browser connection exhaustion
export const MAX_FILES_PER_UPLOAD = 10_000; // API anti-abuse ceiling per upload session

/**
 * Hard ceiling the backend enforces per signed-urls request
 * (`MAX_SIGN_BATCH_SIZE` in services/api/app/schemas/classifications.py).
 * Kept only as the upper bound the client must never exceed — the real
 * just-in-time chunk is `SIGN_BATCH_SIZE` below.
 */
export const MAX_SIGN_BATCH_SIZE = 500;

/**
 * Just-in-time signing chunk. Each signed-urls request signs exactly this
 * many V4 URLs. Keyless (impersonation) signing is one IAM SignBlob round-trip
 * per URL, so the request time scales with the chunk: 25 keeps every call a few
 * seconds — far below the 60s client timeout — even for 644/1000+ uploads, and
 * makes per-file progress start within ~3s instead of after a 50s sign of 500.
 * MUST stay ≤ MAX_SIGN_BATCH_SIZE (runBatchedUpload clamps as a guard).
 */
export const SIGN_BATCH_SIZE = 25;

/**
 * Resilience: bounded retry with exponential backoff. Transient blips (network,
 * 5xx, 429, timeout) are retried so a single hiccup never fails a file or kills
 * a large upload. Deterministic 4xx fail fast.
 */
export const UPLOAD_MAX_ATTEMPTS = 3; // per-file PUT attempts (1 try + 2 retries)
export const UPLOAD_RETRY_BASE_MS = 500; // exponential backoff base for PUT retries
export const SIGN_MAX_ATTEMPTS = 3; // per-chunk signing attempts (1 try + 2 retries)
export const SIGN_RETRY_BASE_MS = 800; // exponential backoff base for signing retries

export const MAX_UPLOAD_FILE_SIZE = 10 * 1024 * 1024;
